import { useState, useMemo, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  Select,
  Input,
  Textarea,
  Toast,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { usePublications } from "@/hooks/usePublications";
import { editorialCategories, type EditorialCategory } from "@/api/editorialCategories.service.js";
import { useCreateTestTestimonial } from "../../hooks/useCreateTestTestimonial.js";
import type { CreateTestTestimonialDialogProps, FormData, Step } from "./types.js";
import styles from "./CreateTestTestimonialDialog.module.css";

function buildCategoryLabel(cat: EditorialCategory, fallback: string): string {
  const parts: string[] = [];
  if (cat.practiceGroup) parts.push(cat.practiceGroup);
  if (cat.practiceArea) parts.push(cat.practiceArea);
  if (cat.tableName) parts.push(cat.tableName);

  const deduped: string[] = [];
  for (const part of parts) {
    if (deduped.length === 0 || deduped[deduped.length - 1] !== part) {
      deduped.push(part);
    }
  }

  if (deduped.length === 0) return fallback;
  if (deduped.length === 1) return deduped[0];
  return deduped.join(" > ");
}

export function CreateTestTestimonialDialog({ onSuccess }: CreateTestTestimonialDialogProps) {
  const currentYear = new Date().getFullYear();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const [selectedPublicationId, setSelectedPublicationId] = useState<number | null>(null);
  const [selectedPublication, setSelectedPublication] = useState("");
  const [selectedEdition, setSelectedEdition] = useState("");
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);

  // Practice-area level state (editorialCategoryId of the selected practice area category)
  const [selectedPracticeAreaCategoryId, setSelectedPracticeAreaCategoryId] = useState<
    number | null
  >(null);

  // Table level state
  const [tableOptions, setTableOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedTableCategoryId, setSelectedTableCategoryId] = useState<number | null>(null);
  const [isLoadingTables, setIsLoadingTables] = useState(false);

  const [regionOptions, setRegionOptions] = useState<{ value: string; label: string }[]>([
    { value: "", label: "Select publication first" },
  ]);
  const [practiceOptions, setPracticeOptions] = useState<{ value: string; label: string }[]>([
    { value: "", label: "Select region first" },
  ]);
  const [isLoadingRegions, setIsLoadingRegions] = useState(false);
  const [isLoadingPractices, setIsLoadingPractices] = useState(false);

  // Maps editorialCategoryId → practiceAreaId for the currently loaded practice area options.
  // Used by handlePracticeAreaChange to look up practiceAreaId without re-fetching.
  const practiceAreaMetaRef = useRef<Map<number, number>>(new Map());

  const [formData, setFormData] = useState<FormData>({
    id: crypto.randomUUID(),
    content: "",
    lawyerName: "",
    code: crypto.randomUUID().split("-")[0],
    companyId: undefined,
    contactId: undefined,
  });

  const { data: publicationsData } = usePublications({
    edition: String(currentYear),
  });

  const { mutate: createTestimonial, loading: isPending } = useCreateTestTestimonial();

  const steps: Step[] = useMemo(
    () => [
      {
        heading: "Publication",
        subtitle: "Choose the publication and edition for this test testimonial",
      },
      {
        heading: "Editorial Category",
        subtitle: "Choose the region and practice area",
      },
      {
        heading: "Testimonial Details",
        subtitle: "Enter the testimonial content and metadata",
      },
    ],
    [],
  );

  const publicationOptions = useMemo(() => {
    const pubs = publicationsData?.publications ?? [];
    return pubs.map(p => ({
      value: String(p.id),
      label: p.publication
        ? `${p.publication} (${p.edition ?? "n/a"})`
        : `Publication ${p.id}${p.edition ? ` (${p.edition})` : ""}`,
    }));
  }, [publicationsData?.publications]);

  useEffect(() => {
    if (!selectedPublicationId) {
      setRegionOptions([{ value: "", label: "Select publication first" }]);
      return;
    }

    const loadRegions = async () => {
      setIsLoadingRegions(true);
      try {
        const response = await editorialCategories.getAll({
          publicationId: selectedPublicationId,
          type: "country",
          limit: 100,
        });

        const uniqueRegions = new Map<number, string>();
        response.editorialCategories?.forEach((cat: EditorialCategory) => {
          if (cat.countryId && cat.country && !uniqueRegions.has(cat.countryId)) {
            uniqueRegions.set(cat.countryId, cat.country);
          }
        });

        const options = Array.from(uniqueRegions.entries())
          .map(([id, name]) => ({
            value: String(id),
            label: name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setRegionOptions([{ value: "", label: "Select region" }, ...options]);
      } catch {
        setRegionOptions([{ value: "", label: "Failed to load regions" }]);
      } finally {
        setIsLoadingRegions(false);
      }
    };

    loadRegions();
  }, [selectedPublicationId]);

  useEffect(() => {
    if (!selectedPublicationId || !selectedRegionId) {
      setPracticeOptions([{ value: "", label: "Select region first" }]);
      practiceAreaMetaRef.current = new Map();
      return;
    }

    const loadPracticeAreas = async () => {
      setIsLoadingPractices(true);
      try {
        const response = await editorialCategories.getAll({
          publicationId: selectedPublicationId,
          countryId: selectedRegionId,
          type: "practice_area",
          limit: 100,
        });

        const metaMap = new Map<number, number>();

        const options =
          response.editorialCategories
            ?.map((cat: EditorialCategory) => {
              // Store editorialCategoryId → practiceAreaId mapping
              if (cat.practiceAreaId !== null && cat.practiceAreaId !== undefined) {
                metaMap.set(cat.id, cat.practiceAreaId);
              }

              return {
                value: String(cat.id),
                label: buildCategoryLabel(cat, `Practice ${cat.id}`),
              };
            })
            .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

        practiceAreaMetaRef.current = metaMap;
        setPracticeOptions([{ value: "", label: "Select practice area" }, ...options]);
      } catch {
        practiceAreaMetaRef.current = new Map();
        setPracticeOptions([{ value: "", label: "Failed to load practice areas" }]);
      } finally {
        setIsLoadingPractices(false);
      }
    };

    loadPracticeAreas();
  }, [selectedPublicationId, selectedRegionId]);

  const resetModalState = () => {
    setCurrentStep(0);
    setSelectedPublicationId(null);
    setSelectedPublication("");
    setSelectedEdition("");
    setSelectedRegionId(null);
    setSelectedPracticeAreaCategoryId(null);
    setTableOptions([]);
    setSelectedTableCategoryId(null);
    setFormData({
      id: crypto.randomUUID(),
      content: "",
      lawyerName: "",
      code: crypto.randomUUID().split("-")[0],
      companyId: undefined,
      contactId: undefined,
    });
  };

  const closeModal = () => {
    setIsOpen(false);
    resetModalState();
  };

  const openModal = () => {
    setIsOpen(true);
  };

  const canProceedStep0 = (selectedPublicationId ?? 0) > 0;
  const canProceedStep1 =
    tableOptions.length > 0
      ? (selectedTableCategoryId ?? 0) > 0
      : (selectedPracticeAreaCategoryId ?? 0) > 0;
  const canSubmit = formData.content.trim().length > 0;

  const goToNextStep = () => {
    if (currentStep === 0 && canProceedStep0) {
      setCurrentStep(1);
    } else if (currentStep === 1 && canProceedStep1) {
      setCurrentStep(2);
    } else if (currentStep === 2 && canSubmit) {
      handleSubmit();
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const finalEditorialCategoryId = selectedTableCategoryId ?? selectedPracticeAreaCategoryId;
    if (!selectedPublicationId || !finalEditorialCategoryId) return;

    try {
      const response = await createTestimonial({
        id: formData.id,
        publicationId: selectedPublicationId,
        publication: selectedPublication,
        edition: selectedEdition,
        editorialCategoryId: finalEditorialCategoryId,
        content: formData.content,
        lawyerName: formData.lawyerName || undefined,
        code: formData.code,
        contentType: undefined,
        companyId: formData.companyId,
        firmRef: formData.companyId,
        contactId: formData.contactId,
      });

      setToast({
        message: `${response.message} (ID: ${response.id})`,
        type: "success",
      });
      closeModal();
      onSuccess?.();
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setToast({
        message: `Failed to create test testimonial: ${error.message}`,
        type: "error",
      });
    }
  };

  const handlePublicationChange = (value: string) => {
    const pubId = Number(value);
    setSelectedPublicationId(pubId);

    const pub = publicationsData?.publications?.find(p => p.id === pubId);
    if (pub) {
      setSelectedPublication(pub.publication ?? "");
      setSelectedEdition(pub.edition ?? "");
    }

    setSelectedRegionId(null);
    setSelectedPracticeAreaCategoryId(null);
    setTableOptions([]);
    setSelectedTableCategoryId(null);
  };

  const handleRegionChange = (value: string) => {
    const regionId = Number(value);
    setSelectedRegionId(regionId);
    setSelectedPracticeAreaCategoryId(null);
    setTableOptions([]);
    setSelectedTableCategoryId(null);
  };

  const handlePracticeAreaChange = async (value: string) => {
    const catId = Number(value);

    setSelectedPracticeAreaCategoryId(catId);
    setSelectedTableCategoryId(null);
    setTableOptions([]);

    // Retrieve the practiceAreaId from the metadata map populated during practice area load
    const paId = practiceAreaMetaRef.current.get(catId) ?? null;

    if (!selectedPublicationId || !paId) return;

    setIsLoadingTables(true);
    try {
      const response = await editorialCategories.getAll({
        publicationId: selectedPublicationId,
        practiceAreaId: paId,
        type: "table",
        limit: 100,
      });

      const cats = response.editorialCategories ?? [];
      if (cats.length === 0) {
        setTableOptions([]);
        return;
      }

      const options = cats
        .map((cat: EditorialCategory) => ({
          value: String(cat.id),
          label: buildCategoryLabel(cat, `Table ${cat.id}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      setTableOptions([{ value: "", label: "Select table" }, ...options]);
    } catch {
      setTableOptions([{ value: "", label: "Failed to load tables" }]);
    } finally {
      setIsLoadingTables(false);
    }
  };

  const handleTableChange = (value: string) => {
    setSelectedTableCategoryId(Number(value) || null);
  };

  const renderStepContent = () => {
    if (currentStep === 0) {
      return (
        <div className={styles.stepContainer}>
          <div className={styles.stepHeader}>
            <h3 className={styles.stepTitle}>{steps[0].heading}</h3>
            <p className={styles.stepSubtitle}>{steps[0].subtitle}</p>
          </div>
          <div className={styles.formGroup}>
            <Select
              label="Publication"
              value={selectedPublicationId ? String(selectedPublicationId) : ""}
              onChange={e => handlePublicationChange(e.target.value)}
              options={publicationOptions}
              required
            />
          </div>
        </div>
      );
    }

    if (currentStep === 1) {
      return (
        <div className={styles.stepContainer}>
          <div className={styles.stepHeader}>
            <h3 className={styles.stepTitle}>{steps[1].heading}</h3>
            <p className={styles.stepSubtitle}>{steps[1].subtitle}</p>
          </div>
          <div className={styles.formGroup}>
            <Select
              label="Region"
              value={selectedRegionId ? String(selectedRegionId) : ""}
              onChange={e => handleRegionChange(e.target.value)}
              options={regionOptions}
              disabled={isLoadingRegions || !selectedPublicationId}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <Select
              label="Practice Area"
              value={selectedPracticeAreaCategoryId ? String(selectedPracticeAreaCategoryId) : ""}
              onChange={e => handlePracticeAreaChange(e.target.value)}
              options={practiceOptions}
              disabled={isLoadingPractices || !selectedRegionId}
              required
            />
          </div>
          {tableOptions.length > 0 && (
            <div className={styles.formGroup}>
              <Select
                label="Table"
                value={selectedTableCategoryId ? String(selectedTableCategoryId) : ""}
                onChange={e => handleTableChange(e.target.value)}
                options={tableOptions}
                disabled={isLoadingTables}
                required
              />
            </div>
          )}
          {isLoadingTables && tableOptions.length === 0 && (
            <div className={styles.formGroup}>
              <Select
                label="Table"
                value=""
                onChange={() => undefined}
                options={[{ value: "", label: "Loading tables..." }]}
                disabled
              />
            </div>
          )}
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className={styles.stepContainer}>
          <div className={styles.stepHeader}>
            <h3 className={styles.stepTitle}>{steps[2].heading}</h3>
            <p className={styles.stepSubtitle}>{steps[2].subtitle}</p>
          </div>
          <div className={styles.formGroup}>
            <Input
              label="ID"
              value={formData.id}
              onChange={e => setFormData(prev => ({ ...prev, id: e.target.value }))}
              required
              helperText="Auto-generated UUID (editable)"
            />
          </div>
          <div className={styles.formGroup}>
            <Textarea
              label="Content"
              value={formData.content}
              onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
              required
              rows={6}
              placeholder="Enter testimonial content..."
            />
          </div>
          <div className={styles.formGroup}>
            <Input
              label="Lawyer Name"
              value={formData.lawyerName}
              onChange={e => setFormData(prev => ({ ...prev, lawyerName: e.target.value }))}
              placeholder="Optional"
            />
          </div>
          <div className={styles.formGroup}>
            <Input
              label="Company ID"
              type="number"
              value={formData.companyId ?? ""}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  companyId: e.target.value ? Number(e.target.value) : undefined,
                }))
              }
              placeholder="Optional"
            />
          </div>
          <div className={styles.formGroup}>
            <Input
              label="Contact ID"
              value={formData.contactId ?? ""}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  contactId: e.target.value || undefined,
                }))
              }
              placeholder="Optional"
              helperText="Contact UUID from the survey system"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const breadcrumbs = useMemo(() => {
    if (currentStep === 0) return [];

    const items: Array<{ id: number; name: string }> = [];

    const publicationName =
      currentStep > 0 && selectedPublication
        ? `${selectedPublication} (${selectedEdition})`
        : steps[0].heading;
    items.push({ id: 0, name: publicationName });

    if (currentStep > 1) {
      const regionName =
        selectedRegionId !== null
          ? regionOptions.find(r => r.value === String(selectedRegionId))?.label || steps[1].heading
          : steps[1].heading;
      items.push({ id: 1, name: regionName });
    }

    return items;
  }, [currentStep, selectedPublication, selectedEdition, selectedRegionId, regionOptions, steps]);

  const isFinalStep = currentStep === 2;
  const isActionDisabled =
    (currentStep === 0 && !canProceedStep0) ||
    (currentStep === 1 && !canProceedStep1) ||
    (currentStep === 2 && (!canSubmit || isPending));

  const actionLabel = isFinalStep ? "Create Test Testimonial" : "Next";

  return (
    <>
      <Button variant="outlined" onClick={openModal} size="small">
        Create Test Testimonial
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        title="Create Test Testimonial"
        size="medium"
        backdropClick={false}
      >
        <div className={styles.modalContent}>
          {breadcrumbs.length > 0 && (
            <nav aria-label="Breadcrumb navigation" className={styles.breadcrumbs}>
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return (
                  <span key={crumb.id} className={styles.breadcrumbItem}>
                    {isLast ? (
                      <span className={styles.breadcrumbCurrent} aria-current="page">
                        {crumb.name}
                      </span>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={styles.breadcrumbLink}
                          onClick={() => setCurrentStep(crumb.id)}
                        >
                          {crumb.name}
                        </button>
                        <span className={styles.breadcrumbSeparator} aria-hidden="true">
                          {" > "}
                        </span>
                      </>
                    )}
                  </span>
                );
              })}
            </nav>
          )}

          <div className={styles.stepContent}>{renderStepContent()}</div>

          <div className={styles.footerActions}>
            <div className={styles.footerLeft}>
              {currentStep > 0 && (
                <Button variant="outlined" onClick={goToPreviousStep}>
                  Back
                </Button>
              )}
            </div>
            <div className={styles.footerRight}>
              <Button variant="outlined" onClick={closeModal}>
                Cancel
              </Button>
              <Button color="primary" onClick={goToNextStep} disabled={isActionDisabled}>
                {isPending ? "Creating..." : actionLabel}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </>
  );
}
